/**
 * Independent Database Backup Sync Service
 * 
 * This service provides completely independent database backup without interfering
 * with local cache operations. It works as a pure backup system that:
 * 1. Never reads from database to update local cache
 * 2. Only writes to database for backup purposes
 * 3. Runs completely in background without blocking UI
 * 4. Has no impact on local cache performance or data
 */

interface BackupQueueItem {
  properties: Property[];
  searchType: string;
  timestamp: number;
  retryCount: number;
}

class DatabaseBackupService {
  private queue: BackupQueueItem[] = [];
  private isProcessing = false;
  private processingInterval: NodeJS.Timeout | null = null;
  private readonly BATCH_SIZE = 3; // Process 3 batches at a time
  private readonly PROCESSING_DELAY = 3000; // 3 seconds between batches
  private readonly MAX_RETRIES = 2;
  private readonly MAX_QUEUE_SIZE = 50; // Smaller queue for backup only

  /**
   * Queue properties for database backup (completely independent)
   */
  public queueProperties(properties: Property[], searchType: string): void {
    if (!properties || properties.length === 0) {
      return;
    }

    // Prevent queue from growing too large
    if (this.queue.length >= this.MAX_QUEUE_SIZE) {
      this.queue = this.queue.slice(-this.MAX_QUEUE_SIZE + 1);
    }

    // Add to backup queue
    this.queue.push({
      properties: [...properties], // Create a copy to avoid reference issues
      searchType,
      timestamp: Date.now(),
      retryCount: 0
    });


    // Start processing if not already running
    this.startProcessing();
  }

  /**
   * Start processing the backup queue
   */
  private startProcessing(): void {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;

    // Process queue with delays to avoid blocking
    this.processQueue();
  }

  /**
   * Process the backup queue
   */
  private async processQueue(): Promise<void> {
    while (this.queue.length > 0) {
      const batch = this.queue.splice(0, this.BATCH_SIZE);
      
      // Process batch in parallel
      const promises = batch.map(item => this.processBackupItem(item));
      await Promise.allSettled(promises);
      
      // Delay between batches to avoid blocking
      if (this.queue.length > 0) {
        await new Promise(resolve => setTimeout(resolve, this.PROCESSING_DELAY));
      }
    }

    this.isProcessing = false;
  }

  /**
   * Process a single backup item
   */
  private async processBackupItem(item: BackupQueueItem): Promise<void> {
    const { properties, searchType, retryCount } = item;

    try {

      // Import the appropriate caching function
      let cacheFunction;
      switch (searchType) {
        case 'rent':
          const { cacheRentalProperties } = await import('./rental-database-helpers');
          cacheFunction = () => cacheRentalProperties(properties);
          break;
        case 'sold':
          const { cacheSoldProperties } = await import('./sold-database-helpers');
          cacheFunction = () => cacheSoldProperties(properties);
          break;
        case 'sale':
        default:
          const { cacheProperties } = await import('./database-helpers');
          cacheFunction = () => cacheProperties(properties);
          break;
      }

      // Execute the backup function
      await cacheFunction();


    } catch (error) {

      // Retry if under max retries
      if (retryCount < this.MAX_RETRIES) {
        
        this.queue.push({
          ...item,
          retryCount: retryCount + 1
        });
      }
    }
  }

  /**
   * Get queue status for monitoring
   */
  public getQueueStatus(): {
    queueSize: number;
    isProcessing: boolean;
    oldestItemAge: number;
  } {
    const now = Date.now();
    const oldestItem = this.queue.reduce((oldest, item) => 
      item.timestamp < oldest.timestamp ? item : oldest, 
      this.queue[0] || { timestamp: now }
    );

    return {
      queueSize: this.queue.length,
      isProcessing: this.isProcessing,
      oldestItemAge: this.queue.length > 0 ? now - oldestItem.timestamp : 0
    };
  }

  /**
   * Clear the backup queue (for testing or emergency)
   */
  public clearQueue(): void {
    this.queue = [];
  }
}

// Create singleton instance
const databaseBackupService = new DatabaseBackupService();

// Export the service
export default databaseBackupService;

// Export convenience functions
export const queuePropertiesForDatabaseBackup = (properties: Property[], searchType: string) => {
  databaseBackupService.queueProperties(properties, searchType);
};

export const getDatabaseBackupStatus = () => {
  return databaseBackupService.getQueueStatus();
};

export const clearDatabaseBackupQueue = () => {
  databaseBackupService.clearQueue();
};
